import axios, { HttpStatusCode } from "axios";
import eventEmitter from "./eventEmitter";
import { USER_REFRESH } from "../constants/routes";

const api = axios.create({
  // baseURL: "https://your-api.com", // Change this to your API base URL
  headers: {
    "Content-Type": "application/json",
  },
});

// Request Interceptor — safety net: attach the stored token when a caller
// didn't set an Authorization header itself (explicit headers always win).
// Uses AxiosHeaders get/set (case-insensitive) so a caller-supplied lowercase
// "authorization" is respected rather than duplicated.
api.interceptors.request.use((config) => {
  const existing = config.headers?.get?.("Authorization") ?? config.headers?.Authorization;
  const existingStr = typeof existing === "string" ? existing.trim() : existing;
  const isMissingOrEmpty =
    !existingStr ||
    existingStr === "Bearer" ||
    existingStr === "Bearer undefined" ||
    existingStr === "Bearer null";
  if (isMissingOrEmpty) {
    try {
      const userInfo = JSON.parse(localStorage.getItem("sid_user") || "{}");
      if (userInfo?.access_token) {
        const value = `Bearer ${userInfo.access_token}`;
        if (config.headers?.set) {
          config.headers.set("Authorization", value);
        } else {
          config.headers.Authorization = value;
        }
      }
    } catch {
      // corrupt storage — let the request proceed unauthenticated
    }
  }
  return config;
});

// Response Interceptor (Handle Unauthorized Errors)
//
// TOKEN_STALE 401s are the backend telling us the subscription state changed
// and the JWT claims are outdated — the documented contract is "silently
// refresh and retry", NOT logout. Every other 401 still logs the user out.
// Without this, logging in on a freshly migrated DB bounced users straight
// back to the login screen (login 200 → first dashboard call TOKEN_STALE 401
// → unauthorized event → localStorage wiped).

let refreshInFlight: Promise<string | null> | null = null;

async function refreshTokens(): Promise<string | null> {
  try {
    const userInfo = JSON.parse(localStorage.getItem("sid_user") || "{}");
    const refreshToken = userInfo?.refresh_token;
    if (!refreshToken) return null;
    // plain axios: must not recurse through this instance's interceptors
    const resp = await axios.post(USER_REFRESH, { refresh_token: refreshToken }, {
      headers: { "Content-Type": "application/json" },
    });
    if (resp.status === 200 && resp.data?.access_token) {
      const updated = { ...userInfo, ...resp.data };
      localStorage.setItem("sid_user", JSON.stringify(updated));
      return resp.data.access_token;
    }
    return null;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (status !== HttpStatusCode.Unauthorized) {
      return Promise.reject(error);
    }

    const detail = error.response?.data?.detail;
    const isTokenStale = typeof detail === "object" && detail?.code === "TOKEN_STALE";
    const original = error.config || {};

    if (isTokenStale && !original._tokenStaleRetried) {
      original._tokenStaleRetried = true;
      // share one refresh across concurrent 401s
      refreshInFlight = refreshInFlight || refreshTokens().finally(() => {
        refreshInFlight = null;
      });
      const newToken = await refreshInFlight;
      if (newToken) {
        const value = `Bearer ${newToken}`;
        if (original.headers?.set) {
          original.headers.set("Authorization", value);
        } else {
          original.headers = { ...(original.headers || {}), Authorization: value };
        }
        return api(original);
      }
    }

    eventEmitter.emit("unauthorized");
    return Promise.reject(error);
  }
);

export default api;
